import * as React from 'react';
import {formatCurrency} from '../../lib/currency';
import {classes, style} from 'typestyle';
import {
  aroundJustified, betweenJustified,
  center,
  centerCenter,
  centerJustified,
  content,
  end,
  horizontal,
  start,
  vertical
} from 'csstips';
import {percent, rem} from 'csx';
import {colorBrand, colorGold, colorSubtle, colorWhite} from '../../theme/color';
import {gradientDark, gradientDrawer} from '../../theme/gradient';
import {mediaMobileOnly, mediaTablet} from '../../theme/media';
import {OrderSchema} from '../../types/schema';
import {formatDate} from '../../lib/date';

type CouponType = 'internal' | 'support_coupon' | 'first_use' | 'second_use' | 'referral_use' | 'golden_ticket';
type CouponStatus = 'used' | 'unused';

/**
 * This is the data shape
 */
export type Coupon = {
  coupon: string;
  currency: string;
  discount: number;
  expires: {
    date: string;
    timestamp: number;
  }
  limit: number;
  order: OrderSchema[]
  status: CouponStatus;
  type: CouponType;
  value: number;
}

type Props = {
  coupon: Coupon;
  i18n?: I18n;
}

type CouponTypeMapping = {[key in CouponType]: string};

type I18n = {
  label: CouponTypeMapping;
  description: CouponTypeMapping;
}

export default class UserCoupon extends React.Component<Props, any> {
  render() {
    const {coupon, i18n = UserCoupon.i18n} = this.props;

    const discount = !!coupon.value
      ? formatCurrency(coupon.value, coupon.currency)
      : `${coupon.discount || 0}%`;

    const timestamp = coupon.expires.timestamp * 1000;
    const hasExpired = timestamp < Date.now();
    const expiry = formatDate(new Date(timestamp));

    const remaining = Math.max(coupon.limit - (coupon.order ? coupon.order.length : 0), 0);
    const usages = hasExpired
      ? 'EXPIRED'
      : remaining;

    return (
      <div className={classes(UserCoupon.styles.container, UserCoupon.getStyle(coupon.type))}>
        <div className={UserCoupon.styles.main}>
          <div className={UserCoupon.styles.meta}>
            <h5>{i18n.label[coupon.type]}</h5>
            <p>{i18n.description[coupon.type]}</p>
          </div>
          <div className={UserCoupon.styles.value}>
            {discount}
          </div>
        </div>
        <div className={UserCoupon.styles.stub}>
          <div>
            <div>usages</div>
            <div>{usages}</div>
          </div>
          <div>
            <div>expires</div>
            <div>{expiry}</div>
          </div>
        </div>
      </div>
    )
  }

  static getStyle = (type: CouponType) => {
    switch (type) {
      case 'referral_use':
        return UserCoupon.styles.typeReferral;

      case 'golden_ticket':
        return UserCoupon.styles.typeGolden;

      case 'first_use':
      case 'second_use':
        return UserCoupon.styles.typeUse;

      default:
        return UserCoupon.styles.typeNormal;
    }
  };

  static i18n: I18n = {
    label: {
      first_use: 'First Track Free',
      second_use: 'First Use Discount',
      golden_ticket: 'Golden Ticket',
      internal: 'Inside Access',
      referral_use: 'Referral',
      support_coupon: 'Support Coupon'
    },
    description: {
      first_use: 'Your first license for your channel is absolutely on us',
      second_use: 'Thanks for using Lickd!',
      golden_ticket: 'Eight free tracks and insider access',
      internal: 'Unlimited usage - Lickd staff',
      referral_use: 'A free track earned by referring a friend!',
      support_coupon: 'Hopefully this patches things up'
    }
  };

  static styles = {
    container: style(
      {
        ...center,
        ...content,
        borderRadius: rem(1),
      },
      mediaMobileOnly({
        ...vertical,
        $nest: {
          '&>div': {
            width: percent(100)
          }
        }
      }),
      mediaTablet({
        ...horizontal,
        height: rem(4.75),
        $nest: {
          '&>div': {
            height: percent(100)
          }
        }
      })
    ),
    typeReferral: style({
      background: gradientDrawer,
      color: colorWhite.toString(),
    }),
    typeUse: style({
      background: colorBrand.darken(0.35).toString(),
      color: colorWhite.toString(),
    }),
    typeGolden: style({
      background: gradientDark,
      color: colorWhite.toString(),
    }),
    typeNormal: style({
      background: colorSubtle.darken(0.25).toString(),
      color: colorWhite.toString(),
    }),
    main: style({
      ...horizontal,
      ...center,
      ...betweenJustified,
      flex: 3,
    }),
    meta: style({
      ...start,
      ...vertical,
      ...centerJustified,
      padding: rem(1),
      height: percent(100),
      flex: 3,
      $nest: {
        '& h5': {
          fontSize: rem(1.125),
          margin: '0 0 0.125rem 0',
        },
        '& p': {
          margin: 0,
          fontSize: rem(0.875)
        }
      }
    }),
    value: style({
      ...end,
      ...vertical,
      ...centerJustified,
      padding: rem(1),
      height: percent(100),
      flex: 1,
      fontSize: rem(2),
      lineHeight: rem(2),
      fontWeight: 200,
    }),
    stub: style(
      {
        ...horizontal,
        ...center,
        ...betweenJustified,
        flex: 1,
        lineHeight: rem(1),
        minWidth: rem(4),
        minHeight: rem(3),
        padding: '1rem',
        $nest: {
          '&>div': {
            ...vertical,
            ...centerCenter,
            flexShrink: 0,
          },
          '&>div:first-child': {
            ...start,
            marginRight: rem(1),
          },
          '&>div:last-child': {
            ...end
          },
          '&>div>div:first-child': {
            fontSize: rem(0.875),
            color: colorWhite.fade(0.5).toString(),
            fontWeight: 500,
            margin: '0 0 0.125rem'
          }
        }
      },
      mediaMobileOnly({
        borderTop: `0.125rem dotted ${colorWhite.fade(0.5).toString()}`,
      }),
      mediaTablet({
        borderLeft: `0.125rem dotted ${colorWhite.fade(0.5).toString()}`,
      })
    ),
  }
}